import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon, { type IconName } from '../components/Brand/Icon';
import DropUploadPanel from '../components/Send/DropUploadPanel';
import { FactList, RouteDiagram, type Fact, type Way } from '../components/Send/Ways';
import { getDropConfig } from '../lib/dropsApi';
import { usePageTitle } from '../hooks/usePageTitle';
import { HOWS, WHATS, choiceSearch, isAvailable, parseChoice, recommended, type How, type What } from '../lib/sendWizard';
import { BurnLinkPanel } from './CreatePage';
import { LivePanel } from './LiveSendPage';
import { EncryptPanel } from './ToolsPage';
import ui from '../styles/ui.module.css';
import styles from './SendPage.module.css';

/**
 * /send — the send wizard (docs/adr/0014): what you're sending, how it
 * should travel, then the form for that pair. The form is the same panel
 * /new, /live and /tools use, so each way behaves the same from either door.
 */

const WHAT_ICONS: Record<What, IconName> = { text: 'text', small: 'file', large: 'box' };
/** How much of the size meter each kind fills: a third, two thirds, all of it. */
const WHAT_SIZE: Record<What, number> = { text: 1, small: 2, large: 3 };

type FactKey = [IconName, string];
const ONLINE: FactKey = ['people', 'online'];
const NEVER_STORED: FactKey = ['notStored', 'neverStored'];
const HIDES_IP: FactKey = ['shield', 'hidesIp'];
const PASSED: FactKey = ['notStored', 'passedThrough'];
const WAY_FACTS: Record<Way, Record<What, FactKey[]>> = {
  drop: {
    text: [['clock', 'within7'], ['flame', 'burns'], ['text', 'text100']],
    small: [['clock', 'keepChosen'], ['flame', 'goneAfter'], ['size', 'paidByTime']],
    large: [['clock', 'keepChosen'], ['flame', 'goneAfter'], ['size', 'paidByTime']],
  },
  live: {
    text: [ONLINE, NEVER_STORED, ['person', 'seeIt']],
    small: [ONLINE, NEVER_STORED, ['size', 'fallback']],
    large: [ONLINE, NEVER_STORED, ['box', 'anySize']],
  },
  relay: {
    text: [HIDES_IP, ONLINE, PASSED, ['size', 'free25']],
    small: [HIDES_IP, ONLINE, PASSED, ['size', 'free25']],
    large: [HIDES_IP, ONLINE, PASSED, ['size', 'pass']],
  },
};
/** Drop for files where stored drops aren't on yet. */
const NOT_YET: FactKey[] = [['text', 'textOnly']];
const LOCK_FACTS: FactKey[] = [['lock', 'lockLocal'], ['arrow', 'lockSend'], ['noAccount', 'lockPassword']];

export default function SendPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Stored drops for files are on only where the Worker says so (production waits for the release gate).
  const [dropsForFiles, setDropsForFiles] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getDropConfig().then(
      (c) => !cancelled && setDropsForFiles(c.maxBytes > 0),
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, []);
  const { what, how, step } = parseChoice(params, dropsForFiles);
  const stepRef = useRef<HTMLDivElement>(null);
  const shownStep = useRef(step);
  usePageTitle(t('pageTitle.send'));

  // The step's content replaces the button that was pressed: start there.
  useEffect(() => {
    if (step === shownStep.current) return;
    shownStep.current = step;
    stepRef.current?.focus();
  }, [step]);

  const facts = (keys: FactKey[]): Fact[] =>
    keys.map(([icon, key]) => ({ icon, strong: t(`send.how.facts.${key}.strong`), rest: t(`send.how.facts.${key}.rest`) }));
  const go = (nextWhat: What | null, nextHow: How | null = null) => navigate({ search: choiceSearch(nextWhat, nextHow) });

  const steps = [
    { n: 1, label: t('send.stepLabel.what'), value: what ? t(`send.what.${what}.title`) : null, to: () => go(null) },
    { n: 2, label: t('send.stepLabel.how'), value: how ? t(`send.how.value.${how}`) : null, to: () => go(what) },
    { n: 3, label: t('send.stepLabel.send'), value: null, to: () => undefined },
  ] as const;

  return (
    <main className={styles.page}>
      <h1 className={styles.title}>{t('send.title')}</h1>
      <p className={styles.lead}>{t('send.lead')}</p>

      <nav aria-label={t('send.progress')}>
        <ol className={styles.progress}>
          {steps.map((s) => {
            const done = s.n < step;
            const current = s.n === step;
            return (
              <li key={s.n} className={styles.progressItem}>
                {done ? (
                  <button type="button" className={styles.progressButton} onClick={s.to}>
                    <span className={`${styles.dot} ${styles.dotDone}`} aria-hidden="true">
                      ✓
                    </span>
                    {s.label}
                    {s.value && <span className={styles.progressValue}>· {s.value}</span>}
                    <span className="visually-hidden"> ({t('send.change')})</span>
                  </button>
                ) : (
                  <span className={`${styles.progressButton} ${current ? '' : styles.progressTodo}`} aria-current={current ? 'step' : undefined}>
                    <span className={`${styles.dot} ${current ? styles.dotCurrent : ''}`} aria-hidden="true">
                      {s.n}
                    </span>
                    {s.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <div ref={stepRef} tabIndex={-1} className={styles.step}>
        {step === 1 && (
          <section aria-labelledby="what-title">
            <h2 id="what-title" className={styles.stepTitle}>
              {t('send.what.title')}
            </h2>
            <ul className={styles.choices}>
              {WHATS.map((w) => (
                <li key={w}>
                  <button type="button" className={`${styles.choice} ${what === w ? styles.choiceOn : ''}`} onClick={() => go(w)} data-testid={`send-what-${w}`}>
                    <span className={styles.choiceIcon}>
                      <Icon name={WHAT_ICONS[w]} size={24} />
                    </span>
                    <span className={styles.choiceTitle}>{t(`send.what.${w}.title`)}</span>
                    <span className={styles.choiceBody}>{t(`send.what.${w}.body`)}</span>
                    <span className={styles.meter} aria-hidden="true">
                      {[1, 2, 3].map((n) => (
                        <span key={n} className={n <= WHAT_SIZE[w] ? styles.meterOn : styles.meterOff} />
                      ))}
                    </span>
                    <span className={styles.choiceLimit}>{t(`send.what.${w}.limit`)}</span>
                  </button>
                </li>
              ))}
            </ul>
            <p className={styles.note}>{t('send.what.note')}</p>
          </section>
        )}

        {step === 2 && what && (
          <section aria-labelledby="how-title">
            <h2 id="how-title" className={styles.stepTitle}>
              {t('send.how.title')}
            </h2>
            <p className={styles.stepLead}>{t('send.how.lead', { what: t(`send.what.${what}.lower`) })}</p>
            <ul className={styles.ways}>
              {HOWS.map((h) => {
                const available = isAvailable(what, h, dropsForFiles);
                const best = recommended(what) === h;
                return (
                  <li key={h} className={`${styles.way} ${best ? styles.wayBest : ''} ${available ? '' : styles.wayOff}`} data-testid={`send-way-${h}`}>
                    <div className={styles.wayTop}>
                      <p className={styles.eyebrow}>{t(`send.how.${h}.eyebrow`)}</p>
                      <span className={best ? styles.badgeStrong : available ? styles.badge : styles.badgeOff}>{t(`send.how.badge.${badgeKey(h, what, best, available)}`)}</span>
                    </div>
                    <h3 className={styles.wayTitle}>
                      {/* The whole card is this button's target (see .wayButton::after). */}
                      <button
                        type="button"
                        className={styles.wayButton}
                        onClick={() => go(what, h)}
                        disabled={!available}
                        aria-describedby={`send-facts-${h}`}
                        data-testid={`send-how-${h}`}
                      >
                        {t(`send.how.${h}.title`)}
                      </button>
                    </h3>
                    <p className={styles.wayLead}>{t(`send.how.${h}.lead`)}</p>
                    <RouteDiagram way={h} muted={!available} />
                    <FactList id={`send-facts-${h}`} facts={facts(available || h !== 'drop' ? WAY_FACTS[h][what] : NOT_YET)} muted={!available} />
                  </li>
                );
              })}
            </ul>
            {isAvailable(what, 'lock') && (
              <p className={styles.note}>
                <button type="button" className={styles.linkButton} onClick={() => go(what, 'lock')}>
                  {t('send.how.lock')}
                </button>
              </p>
            )}
            <button type="button" className={`${ui.buttonSecondary} ${styles.back}`} onClick={() => go(null)}>
              ← {t('send.how.back')}
            </button>
          </section>
        )}

        {step === 3 && what && how && (
          <>
            <div className={styles.sendRow}>
              <div className={styles.panel}>
                <StepThree what={what} how={how} />
              </div>
              <aside className={styles.travels} aria-labelledby="travels-title">
                <p className={styles.eyebrow}>{t('send.travels')}</p>
                <h2 id="travels-title" className={styles.travelsTitle}>
                  {t(`send.how.value.${how}`)}
                </h2>
                {how !== 'lock' && <RouteDiagram way={how} />}
                <FactList facts={facts(how === 'lock' ? LOCK_FACTS : WAY_FACTS[how][what])} />
              </aside>
            </div>
            <button type="button" className={`${ui.buttonSecondary} ${styles.back}`} onClick={() => go(what)}>
              ← {t('send.how.back')}
            </button>
          </>
        )}
      </div>
    </main>
  );
}

/** The form for the chosen pair. Keyed so changing the choice starts a fresh form. */
function StepThree({ what, how }: { what: What; how: How }) {
  const key = `${what}-${how}`;
  if (how === 'drop') return what === 'text' ? <BurnLinkPanel key={key} /> : <DropUploadPanel key={key} />;
  if (how === 'lock') return <EncryptPanel key={key} kind={what === 'text' ? 'text' : 'file'} />;
  return <LivePanel key={key} kind={what === 'text' ? 'text' : 'files'} route={how === 'relay' ? 'relay' : 'auto'} />;
}

function badgeKey(how: (typeof HOWS)[number], what: What, best: boolean, available: boolean): string {
  if (best) return 'recommended';
  if (!available) return 'notYet';
  if (how === 'live') return 'nothingStored';
  if (how === 'drop') return 'needsPass';
  if (what === 'large') return 'pass';
  return what === 'small' ? 'free25' : 'free';
}
